// src/components/artwork/modals/LocationModal.jsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";

const LocationModal = ({ artwork, onChangeLocation, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Charger les emplacements du client connecté
  useEffect(() => {
    const fetchLocations = async () => {
      if (!profile?.id) return;

      try {
        setIsLoading(true);

        const { data, error } = await supabase
          .from("locations")
          .select("*")
          .eq("client_id", profile.id);

        if (error) throw error;

        console.log("[LocationModal] Locations chargées :", data);
        setLocations(data || []);
      } catch (err) {
        console.error("Erreur chargement locations dans le modal:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLocations();
  }, [profile?.id]);

  // Filtrer les emplacements différents de l’emplacement actuel
  const availableLocations = locations.filter(
    (loc) => loc?.id && loc.id !== artwork?.location_id
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose} // clic sur le backdrop → ferme
    >
      <div
        className="bg-white p-8 rounded-md max-w-lg w-full"
        onClick={(e) => e.stopPropagation()} // bloque la fermeture si on clique dans la modale
      >
        <div className="flex justify-between items-start mb-6">
          <h3 className="font-serif text-xl">{t("artworkModals.location.title")}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-black"
            aria-label={t("artworkModals.common.close")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-4">
            {t("artworkModals.location.description")}
          </p>

          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto" />
              <p className="mt-2 text-gray-600">{t("artworkModals.location.loading")}</p>
            </div>
          ) : availableLocations.length > 0 ? (
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {availableLocations.map((location) => (
                <div
                  key={location.id}
                  className="p-3 border rounded cursor-pointer transition-colors hover:bg-gray-50"
                  onClick={() => {
                    onChangeLocation(location.id);
                    onClose();
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium">{location.name}</h4>
                      <p className="text-sm text-gray-600">
                        {location.type || t("artworkModals.location.unspecifiedType")}
                      </p>
                      {location.address && (
                        <p className="text-xs text-gray-500 mt-1">
                          📍 {location.address}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded">
              <p className="text-gray-500">{t("artworkModals.location.empty")}</p>
            </div>
          )}
        </div>

        <div className="flex justify-between">
          <button
            type="button"
            onClick={onClose}
            className="py-2 px-4 border rounded hover:bg-gray-50"
          >
            {t("artworkModals.common.cancel")}
          </button>
          <button
            type="button"
            onClick={() => navigate("/add-location")}
            className="py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {t("artworkModals.location.actions.add")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationModal;
